import React from 'react';
import {
  useEnvironment,
  useExperiments,
  useTranslation,
} from '@wix/yoshi-flow-editor';
import { classes, st } from './IntakeFormSection.st.css';
import { IntakeFormSectionDataHook } from './dataHooks';
import {
  Text,
  TextButtonPriority,
  Spinner,
  SpinnerTypes,
  TextButton,
  HoverStyles,
} from 'wix-ui-tpa/cssVars';
import SelectFill from 'wix-ui-icons-common/on-stage/SelectFill';

import { useFormActions } from '../../Hooks/useFormActions';
import { useFormComponentContext } from '../../Hooks/useFormComponentContext';
import { useScrollToSectionOnError } from '../../Hooks/useScrollToSectionOnError';
import { ExperimentsConsts } from '../../../../consts/experiments';
import {
  IntakeForms,
  IntakeForm,
  IntakeFormStatus,
} from '../../../../utils/state/types';
import { mapToArray } from '../../../../utils';
import { getErrorByType } from '../../../../utils/errors/errors';
import { IntakeFormErrorType, FormError } from '../../../../types/errors';
import {
  SectionMessage,
  SectionMessageType,
} from '../SectionMessage/SectionMessage';

const IntakeFormSection: React.FC<{
  intakeForms?: IntakeForms;
  errors?: FormError[];
}> = ({ intakeForms, errors = [] }) => {
  const { isMobile, isRTL } = useEnvironment();
  const { openIntakeFormLightbox, removeErrors } = useFormActions();
  const { wixFormValues } = useFormComponentContext();
  const { experiments } = useExperiments();
  const { t } = useTranslation();

  const sectionRef = useScrollToSectionOnError(errors, [
    IntakeFormErrorType.REQUIRED_INTAKE_FORM_NOT_COMPLETED,
    IntakeFormErrorType.EMAIL_MISMATCH_WITH_INTAKE_FORM,
  ]);

  const isUOUIntakeFormsIntegrationEnabled = experiments.enabled(
    ExperimentsConsts.UOUIntakeFormsIntegration,
  );

  const intakeFormError = getErrorByType({
    errors,
    errorType: IntakeFormErrorType,
  });

  if (!isUOUIntakeFormsIntegrationEnabled || !intakeForms) {
    return null;
  }

  const intakeFormsArray = mapToArray<IntakeForm>(intakeForms);

  if (intakeFormsArray.length === 0) {
    return null;
  }

  const getErrorText = (errorType: IntakeFormErrorType) => {
    return (
      intakeFormError.params?.message ||
      t(`app.intake-form-section.error.${errorType.toLowerCase()}`)
    );
  };

  const handleClick = (formId: string) => {
    openIntakeFormLightbox(formId, wixFormValues);
    removeErrors([IntakeFormErrorType.REQUIRED_INTAKE_FORM_NOT_COMPLETED]);
  };

  return (
    <div
      ref={sectionRef}
      className={st(classes.root, { isMobile, isRTL })}
      data-hook={IntakeFormSectionDataHook.ROOT}
    >
      <div className={classes.sectionHeader}>
        <Text
          className={classes.sectionTitle}
          data-hook={IntakeFormSectionDataHook.SECTION_TITLE}
        >
          {t('app.intake-form-section.title.default')}
        </Text>
        <Text
          className={classes.subtitle}
          data-hook={IntakeFormSectionDataHook.SECTION_SUBTITLE}
        >
          {t('app.intake-form-section.subtitle.default')}
        </Text>
      </div>

      {intakeFormsArray.map((form) => {
        const isCompletedInSession = form.status === IntakeFormStatus.COMPLETED;

        return (
          <div
            key={form.formId}
            className={classes.formCard}
            data-hook={IntakeFormSectionDataHook.FORM_CARD}
            data-form-id={form.formId}
            onClick={() => handleClick(form.formId!)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClick(form.formId!);
              }
            }}
          >
            <div
              className={classes.container}
              data-hook={IntakeFormSectionDataHook.SECTION_CONTAINER}
            >
              <div className={classes.titleContainer}>
                {isCompletedInSession && (
                  <SelectFill
                    className={classes.completedIcon}
                    data-hook={IntakeFormSectionDataHook.COMPLETED_ICON}
                    aria-hidden
                  />
                )}
                {form.isLoading ? (
                  <Spinner type={SpinnerTypes.slim} diameter={24} />
                ) : (
                  <Text
                    className={classes.formTitle}
                    data-hook={IntakeFormSectionDataHook.FORM_TITLE}
                  >
                    {form.formName}
                    {form.status === IntakeFormStatus.REQUIRED && (
                      <span
                        className={classes.requiredIndicator}
                        data-hook={IntakeFormSectionDataHook.REQUIRED_INDICATOR}
                        aria-hidden="true"
                      >
                        *
                      </span>
                    )}
                  </Text>
                )}
              </div>
              <TextButton
                hoverStyle={HoverStyles.underline}
                priority={TextButtonPriority.primaryLink}
                data-hook={IntakeFormSectionDataHook.FILL_FORM_BUTTON}
                aria-label={t('app.intake-form-section.button.text')}
              >
                {t('app.intake-form-section.button.text')}
              </TextButton>
            </div>
          </div>
        );
      })}
      {intakeFormError && (
        <SectionMessage
          type={SectionMessageType.Error}
          text={getErrorText(intakeFormError.errorType as IntakeFormErrorType)}
          className={classes.errorNotification}
        />
      )}
    </div>
  );
};

export default IntakeFormSection;
